/**
 * FELVORA — Línea Inmobiliaria: Asesores (F2 A3).
 * Alta rápida, edición de comisión, cartera del asesor. dueno_inmo/coordinador_inmo
 * ven todos; asesor solo el propio.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users2, Plus, Percent, BadgeCheck, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { nombre: "", email: "", telefono: "", password: "", comision_default_pct: 3.0, comision_notas: "" };

export default function Asesores() {
  const { user } = useAuth();
  const puedeGestionar = ["dueno_inmo", "coordinador_inmo", "admin_entidad"].includes(user?.role);
  const [asesores, setAsesores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingComision, setEditingComision] = useState(null);
  const [carteraOpen, setCarteraOpen] = useState(false);
  const [cartera, setCartera] = useState(null);

  const load = () => api.get("/inmobiliaria/asesores").then((r) => setAsesores(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const crearAsesor = async () => {
    try {
      await api.post("/inmobiliaria/asesores", form);
      toast.success("Asesor dado de alta");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear asesor");
    }
  };

  const guardarComision = async () => {
    try {
      await api.put(`/inmobiliaria/asesores/${editingComision.id}/comision`, {
        comision_default_pct: editingComision.comision_default_pct,
        comision_notas: editingComision.comision_notas,
      });
      toast.success("Comisión actualizada");
      setEditingComision(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const toggleActivo = async (a) => {
    try {
      await api.put(`/inmobiliaria/asesores/${a.id}/activo`, { activo: !a.activo });
      toast.success(`Asesor ${!a.activo ? "activado" : "desactivado"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const verCartera = async (id) => {
    try {
      const r = await api.get(`/inmobiliaria/asesores/${id}/cartera`);
      setCartera(r.data);
      setCarteraOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="asesores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <Users2 className="mr-2 inline h-6 w-6 text-emerald-700" /> Asesores y comisiones
          </h1>
          <p className="text-sm text-slate-500">
            Gestiona el equipo comercial. La comisión default aplica a operaciones cerradas por el asesor.
          </p>
        </div>
        {puedeGestionar && (
          <Button data-testid="btn-nuevo-asesor" onClick={() => setOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Nuevo asesor
          </Button>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">{asesores.length} asesor(es)</CardTitle></CardHeader>
        <CardContent>
          <div className="divide-y divide-slate-200">
            {asesores.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`asesor-row-${a.id}`}>
                <div>
                  <p className="font-semibold text-slate-900">{a.name} {a.activo ? null : <span className="ml-2 text-xs text-slate-400">(desactivado)</span>}</p>
                  <p className="text-xs text-slate-500">{a.email} · {a.telefono || "—"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    <Percent className="mr-1 inline h-3 w-3" />{a.comision_default_pct?.toFixed(1) ?? "0.0"}%
                  </Badge>
                  <Button size="sm" variant="outline" data-testid={`btn-cartera-${a.id}`} onClick={() => verCartera(a.id)}>Cartera</Button>
                  {puedeGestionar && (
                    <>
                      <Button size="sm" variant="ghost" data-testid={`btn-comision-${a.id}`} onClick={() => setEditingComision({ ...a })}>Editar comisión</Button>
                      <Button size="sm" variant="ghost" data-testid={`btn-toggle-${a.id}`} onClick={() => toggleActivo(a)}>
                        {a.activo ? <XCircle className="h-4 w-4 text-red-500" /> : <BadgeCheck className="h-4 w-4 text-emerald-600" />}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {asesores.length === 0 && <p className="py-6 text-center text-sm text-slate-500">No hay asesores registrados.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Modal alta */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuevo asesor</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input data-testid="asesor-nombre" placeholder="Nombre completo" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
            <Input data-testid="asesor-email" placeholder="Correo institucional" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input data-testid="asesor-telefono" placeholder="Teléfono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            <Input data-testid="asesor-password" placeholder="Contraseña temporal (≥6)" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600 whitespace-nowrap">Comisión default:</label>
              <Input type="number" step="0.1" data-testid="asesor-comision" value={form.comision_default_pct}
                onChange={(e) => setForm({ ...form, comision_default_pct: parseFloat(e.target.value) || 0 })} />
              <span className="text-sm">%</span>
            </div>
            <Textarea placeholder="Notas de comisión (opcional)" rows={2} value={form.comision_notas} onChange={(e) => setForm({ ...form, comision_notas: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="btn-guardar-asesor" onClick={crearAsesor}>Crear asesor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal editar comisión */}
      <Dialog open={!!editingComision} onOpenChange={(v) => !v && setEditingComision(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Comisión de {editingComision?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm">Porcentaje:</label>
              <Input type="number" step="0.1" value={editingComision?.comision_default_pct || 0}
                onChange={(e) => setEditingComision({ ...editingComision, comision_default_pct: parseFloat(e.target.value) || 0 })} />
              <span>%</span>
            </div>
            <Textarea rows={3} value={editingComision?.comision_notas || ""}
              onChange={(e) => setEditingComision({ ...editingComision, comision_notas: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingComision(null)}>Cancelar</Button>
            <Button onClick={guardarComision}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal cartera */}
      <Dialog open={carteraOpen} onOpenChange={setCarteraOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Cartera del asesor</DialogTitle></DialogHeader>
          {cartera && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                <Badge>Inmuebles: {cartera.totales.inmuebles}</Badge>
                <Badge>Disponibles: {cartera.totales.inmuebles_disponibles}</Badge>
                <Badge>Prospectos activos: {cartera.totales.prospectos_activos}</Badge>
              </div>
              <div>
                <p className="font-semibold">Inmuebles asignados</p>
                <ul className="ml-4 list-disc text-xs">
                  {cartera.inmuebles.slice(0, 20).map((i) => (
                    <li key={i.id}>{i.direccion} · <span className="capitalize">{i.estatus}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold">Prospectos</p>
                <ul className="ml-4 list-disc text-xs">
                  {cartera.prospectos.slice(0, 20).map((p) => (
                    <li key={p.id}>{p.nombre} · <span className="capitalize">{p.etapa}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
